import moment from "moment";
import React, { useEffect, useState } from "react";
import { fetchRoutesWayNotifications, fetchProfileNotifications } from "src/services/routes";
import { useAppSelector } from "src/store";

export interface INotification {
  liveRouteId: string;
  latitude: number;
  longitude: number;
  totalDistance: number;
  distanceTraveled: number;
  createdAt: string;
  onRoute: boolean;
  tripId: string;
  status: string;
  startDateTime: string;
  driverId: string;
  assetId: string;
  driverName: string;
  assetName: string;
  accountId: string;
  destinyName: string;
  originName?: string;

  type?:
    | "URGENT_ALERT"
    | "TRIP_ACCEPT"
    | "TRIP_REFUSE"
    | "TRIP_PROPOSED"
    | "TRIP_DEVIATION"
    | "AVOID_AREA"
    | "INTEREST"
    | "SUGGESTED_ROUTE_ECONOMIC"
    | "SUGGESTED_ROUTE_FASTEST"
    | "SUGGESTED_ROUTE_LESS_CONSUMPTION"
    | "SUGGESTED_ROUTE_NOT_MAPPED"
    | "PACKAGES_RECEIVED_QUESTION"
    | "PACKAGES_NOT_RECEIVED_REMINDER"
    | "PACKAGES_DELAYED_DEALER"
    | "TRIP_SYSTEM_FINISHED"
    | "TRIP_STATUS_UPDATE_REQUIRED";
  proposedDate?: string;
  packageName?: string;
  packageId?: string;
}

export type TNotificationsContext = {
  total: number;
  notifications: Array<INotification>;
  today: Array<INotification>;
};

export const NotificationsContext = React.createContext<TNotificationsContext>({
  total: 0,
  notifications: [],
  today: [],
});

export const NotificationsProvider = ({ children }: { children: React.JSX.Element }) => {
  const [total, setTotal] = useState(0);
  const [today, setToday] = useState<Array<INotification>>([]);
  const [notifications, setNotifications] = useState<Array<INotification>>([]);
  const todayM = moment().format("YYYY-MM-DD");
  const userProfile = useAppSelector((state) => state.login.userProfile);

  // Extrair driverId do userProfile (formato: "prod-rio-users:uuid" ou apenas "uuid")
  const getDriverId = (): string | null => {
    if (!userProfile?.sub) return null;
    // Se contém ":", pegar a parte após ":"
    if (userProfile.sub.includes(":")) {
      return userProfile.sub.split(":")[1];
    }
    return userProfile.sub;
  };

  const handleNotifications = async () => {
    try {
      // Buscar notificações de rotas
      const routesResponse = await fetchRoutesWayNotifications();
      const routeNotifications: INotification[] = routesResponse.data?.result || [];
      routeNotifications.push({
        type: "URGENT_ALERT",
        driverName: "João Aparecido da Silva",
        assetName: "E-Truck - 004",
        destinyName: "Cajamar, SP, Brasil",
        createdAt: moment().toISOString(),
        status: "URGENT",
        tripId: "cf6022c1-a917-4793-ab07-f8507983c1f6",
        driverId: "9adbd5e9-3ead-4457-88d7-ee32ba6c3118",
        assetId: "02899364-958e-34c5-8141-cd3b3534c6c3",
        accountId: "5f4d3c8f-cfb8-487e-b3d1-535e776403dd",
        liveRouteId: "",
        latitude: 0,
        longitude: 0,
        totalDistance: 0,
        distanceTraveled: 0,
        onRoute: false,
        startDateTime: moment().toISOString(),
      });

      // Buscar notificações de profile (inclui pacotes)
      let profileNotifications: INotification[] = [];
      const driverId = getDriverId();
      if (driverId) {
        try {
          const profileResponse = await fetchProfileNotifications(driverId, false);
          const profileData = profileResponse.data?.result;

          // Mapear notificações de profile para INotification
          if (profileData?.notifications?.data) {
            profileNotifications = profileData.notifications.data.map((notif: any) => {
              // Converter LocalDateTime para ISO string se necessário
              const startDate = notif.startDate
                ? typeof notif.startDate === "string"
                  ? notif.startDate
                  : new Date(notif.startDate).toISOString()
                : new Date().toISOString();

              return {
                type: notif.type,
                createdAt: startDate,
                packageName: notif.packageName,
                packageId: notif.entityId,
                // Campos obrigatórios com valores padrão
                liveRouteId: "",
                latitude: 0,
                longitude: 0,
                totalDistance: 0,
                distanceTraveled: 0,
                onRoute: false,
                tripId: "",
                status: "",
                startDateTime: startDate,
                driverId: notif.actorDriverId || driverId || "",
                assetId: "",
                driverName: "",
                assetName: "",
                accountId: "",
                destinyName: "",
              };
            });
          }
        } catch (profileError) {
          console.warn("Erro ao buscar notificações de profile:", profileError);
        }
      }

      // Combinar ambas as listas
      const allNotifications = [...routeNotifications, ...profileNotifications];

      const todayItems = allNotifications.filter(
        (item: INotification) => moment(item.createdAt).format("YYYY-MM-DD") === todayM
      );

      const otherItems = allNotifications.filter(
        (item: INotification) => moment(item.createdAt).format("YYYY-MM-DD") !== todayM
      );

      setTotal(todayItems.length + otherItems.length);
      setToday(todayItems);
      setNotifications(otherItems);
    } catch (error) {
      console.log("Erro ao buscar notificações:", error);
    }
  };

  useEffect(() => {
    handleNotifications();

    const interval = setInterval(() => {
      handleNotifications();
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <NotificationsContext.Provider
      value={{
        total,
        notifications: notifications,
        today: today,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
